/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { ActivityIndicator, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing';
import { posOperationalAlertsStyles as alertStyles } from './posOperationalAlerts.styles';

type BannerProps = {
  label: string;
  testID: string;
  primaryActionLabel: string;
  onPrimaryAction: () => void;
  secondaryActionLabel: string;
  onSecondaryAction: () => void;
  primaryLoading?: boolean;
};

function PosOperationalAlertBanner({
  label,
  testID,
  primaryActionLabel,
  onPrimaryAction,
  secondaryActionLabel,
  onSecondaryAction,
  primaryLoading = false,
}: BannerProps) {
  return (
    <View style={alertStyles.mpqrCard} testID={testID}>
      <Text style={alertStyles.mpqrText}>{label}</Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginTop: spacing.xs }}>
        <TouchableOpacity
          onPress={onPrimaryAction}
          disabled={primaryLoading}
          accessibilityRole="button"
          accessibilityLabel={primaryActionLabel}
        >
          {primaryLoading ? (
            <ActivityIndicator size="small" />
          ) : (
            <Text style={alertStyles.mpqrLink}>{primaryActionLabel}</Text>
          )}
        </TouchableOpacity>
        <TouchableOpacity
          onPress={onSecondaryAction}
          accessibilityRole="button"
          accessibilityLabel={secondaryActionLabel}
        >
          <Text style={alertStyles.mpqrLink}>{secondaryActionLabel}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

type ReadyBannerProps = {
  label: string;
  ticketId: string;
  onDismiss: (ticketId: string) => void;
};

function ReadyForPickupBanner({ label, ticketId, onDismiss }: ReadyBannerProps) {
  return (
    <View style={alertStyles.readyCard} testID={`pos-ready-banner-${ticketId}`}>
      <View style={alertStyles.readyRow}>
        <View style={alertStyles.readyIcon}>
          <Ionicons name="bag-check-outline" size={18} color="#FFFFFF" />
        </View>
        <View style={alertStyles.readyCopy}>
          <Text style={alertStyles.readyKicker}>Listo en cocina</Text>
          <Text style={alertStyles.readyTitle}>{label}</Text>
        </View>
      </View>
      <TouchableOpacity
        onPress={() => onDismiss(ticketId)}
        style={alertStyles.dismissBtn}
        accessibilityRole="button"
        accessibilityLabel="Entendido"
      >
        <Text style={alertStyles.dismissLabel}>Entendido</Text>
      </TouchableOpacity>
    </View>
  );
}

type Props = {
  mpQrBanner: { label: string } | null;
  onResumeMpQr: () => void;
  onOpenMpQrInVentas: () => void;
  readyForPickup?: Array<{ id: string; refLabel: string }>;
  onDismissReady?: (ticketId: string) => void;
};

export function PosOperationalAlertsBand({
  mpQrBanner,
  onResumeMpQr,
  onOpenMpQrInVentas,
  readyForPickup = [],
  onDismissReady,
}: Props) {
  if (!mpQrBanner && readyForPickup.length === 0) return null;

  return (
    <View style={alertStyles.band} testID="pos-operational-alerts-band">
      {readyForPickup.map((ticket) => (
        <ReadyForPickupBanner
          key={ticket.id}
          ticketId={ticket.id}
          label={`Pedido ${ticket.refLabel} — retirá en mostrador`}
          onDismiss={onDismissReady ?? (() => undefined)}
        />
      ))}
      {mpQrBanner ? (
        <PosOperationalAlertBanner
          testID="pos-mpqr-pending-banner"
          label={mpQrBanner.label}
          primaryActionLabel="Retomar QR"
          onPrimaryAction={onResumeMpQr}
          secondaryActionLabel="Ver en Ventas"
          onSecondaryAction={onOpenMpQrInVentas}
        />
      ) : null}
    </View>
  );
}
